/*
  Shintop announcement ticker — holds the marquee at one reading pace on every screen.

  CSS alone can't do this. The track travels exactly one group-width per cycle, and the group
  is `max(100vw, content)`, so a fixed duration is not a speed: round 7's 16s ran ~107px/s at
  1440px and ~125px/s on a wider monitor — the bigger the screen, the faster the text, which
  is backwards. This measures the group and sets the duration from --shintop-ticker-speed
  (px/s, declared in component-shintop-ticker.css beside the rest of the tokens).

  Degrades to nothing: with JS off, the duration the section rendered inline still runs; under
  prefers-reduced-motion there is no animation to tune and this stays out of the way.
*/
class ShintopTicker extends HTMLElement {
  connectedCallback() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onResize = this.debounce(() => this.tune(), 250);

    this.tune();

    // Web fonts land after first paint and change every measurement with them.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.tune());
    }

    window.addEventListener('resize', this.onResize);
    this.reducedMotion.addEventListener('change', () => this.tune());
    this.watchPause();
  }

  /*
    The pause/play control — WCAG 2.2 SC 2.2.2 Pause, Stop, Hide (Level A). Audit 2026-09-13,
    S06-03, P1: the marquee started on its own, ran one 42.28 s iteration `infinite`, and the
    only pause was `:hover` (mouse) and a `:focus-within` with nothing inside it to focus.

    All this does is move `aria-pressed`. The pause itself is CSS reading that same attribute
    (component-shintop-ticker.css), so the state the button ANNOUNCES and the state the strip is
    IN are one fact rather than two that have to agree.

    The name does NOT move (audit 2026-10-14, S03-022). It used to swap to "Play announcements"
    on the press, and with the pressed state beside it a screen reader heard "Play announcements,
    pressed" — a name for the next action and a state for the current one, which contradict. A
    toggle's name is constant and `aria-pressed` is its state: "Pause announcements, pressed".

    The markup is server-rendered and hidden until this element is `:defined`, so the control
    appears exactly when it can work.
  */
  watchPause() {
    const toggle = this.querySelector('[data-shintop-ticker-toggle]');
    if (!toggle) return;

    toggle.addEventListener('click', () => {
      const paused = toggle.getAttribute('aria-pressed') !== 'true';
      toggle.setAttribute('aria-pressed', String(paused));
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  tune() {
    if (this.reducedMotion.matches) return;

    const group = this.querySelector('.shintop-ticker__group');
    if (!group) return;

    const distance = group.getBoundingClientRect().width;
    const speed = parseFloat(getComputedStyle(this).getPropertyValue('--shintop-ticker-speed'));
    if (!distance || !speed) return;

    this.style.setProperty('--shintop-ticker-dur', `${(distance / speed).toFixed(2)}s`);
  }

  debounce(fn, wait) {
    let timer;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }
}

if (!customElements.get('shintop-ticker')) {
  customElements.define('shintop-ticker', ShintopTicker);
}
